import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { WizardStep } from '../../../../../shared/controls/wizard-control/wizard-step.interface';
import { asyncScheduler, Observable, scheduled } from 'rxjs';

import { AgeingBatchResult, AgeingService, AgeingWorksheetSelectionItem, AgeingWorksheetInput, ClientService, ClientAreaService, WorkbookService } from '../../../../../../service/proxy';
import { ToastService } from '../../../../../shared/toast/toast.service';
import { ErrorService } from '../../../../../shared/services/error-service';
import { DialogService } from '../../../../../shared/controls/message-dialog/message-dialog.service';

@Component({
    selector: 'ageing-sheet-selection',
    templateUrl: './ageing-sheet-selection.component.html',
    styleUrls: ['./ageing-sheet-selection.component.scss'],

    imports: [
        CommonModule,
        FormsModule
    ],

    providers: [
        AgeingService,
        WorkbookService,
        ClientService,
        ClientAreaService,
        WorkbookService,
        ToastService,
        ErrorService
    ]
})
export class AgeingSheetSelectionComponent implements OnInit, WizardStep {
    @ViewChild('form')
    form!: NgForm;

    batchId: number = 0;
    isWorksheetSelected: boolean = false;
    loading: boolean = false;

    public clients: { id: number; name: string; }[] = [];
    public selectedClientId: number = 0;

    public clientAreas: { id: number; name: string; }[] = [];
    public selectedClientAreaId: number = 0;

    public batch: AgeingBatchResult = new AgeingBatchResult();
    public worksheets: any[] = [];
    public worksheetSelection: AgeingWorksheetSelectionItem[] = [];
    public usedClientAreaIds: Set<number> = new Set<number>();

    constructor(
        private route: ActivatedRoute,
        private ageingService: AgeingService,
        private clientAreaService: ClientAreaService,
        private workbookService: WorkbookService,
        private toastService: ToastService,
        private errorService: ErrorService,
        private dialogService: DialogService,
    ) {}

    ngOnInit(): void {
        this.route.queryParamMap.subscribe(params => {
            this.selectedClientId = Number(params.get('clientId'));
            this.getClientAreas();
        });
    }

    getBatch() {
        this.ageingService.getAgeingBatch(this.batchId)
            .subscribe((result) => {
                this.batch = result;

                if (!this.selectedClientId && result.clientId) {
                    this.selectedClientId = result.clientId;
                }

                this.getClientAreas();
                this.getUsedClientAreas();
                this.getWorksheetSelection();
            }
        );
    }

    getUsedClientAreas(): void {
        if (!this.selectedClientId || !this.batch?.periodId) {
            return;
        }

        this.ageingService.getUsedAgeingClientAreas(this.batch.periodId, this.selectedClientId)
            .subscribe({
                next: (result: any) => {
                    this.usedClientAreaIds = new Set<number>(result?.clientAreaIds ?? []);
                },
                error: () => {
                    this.usedClientAreaIds = new Set<number>();
                }
            });
    }

    onClientAreaSelected(selection: AgeingWorksheetSelectionItem, clientAreaId: number | undefined): void {
        if (!clientAreaId || !this.usedClientAreaIds.has(Number(clientAreaId))) {
            return;
        }

        const area = this.clientAreas.find(a => a.id === Number(clientAreaId));
        const areaName = area?.name ?? 'this policy';

        this.dialogService
            .show(
                'Ageing Already Imported',
                `An ageing has already been imported for "${areaName}" in this period. Do you want to continue with this selection?`,
                'Warning'
            )
            .subscribe((confirmed: boolean) => {
                if (!confirmed) {
                    selection.clientAreaId = undefined;
                }
            });
    }

    getWorksheetSelection(): void {
        this.ageingService.getAgeingWorksheetSelection(this.batchId)
            .subscribe({
                next: (data) => {
                    if (data && data.results && data.results.length > 0) {
                        this.worksheetSelection = data.results.map((item: any) => {
                            const selection = new AgeingWorksheetSelectionItem();
                            selection.worksheet = item.worksheetName;
                            selection.selected = item.isSelected;
                            selection.clientAreaId = item.clientAreaId;
                            return selection;
                        });
                    }
                    else {
                        this.getWorksheets();
                    }
                },
                error: (error) => {
                    this.errorService.handleError(error);
                }
            });
    }

    getClientAreas(): void {
        if (!this.selectedClientId) {
            return;
        }

        this.clientAreaService.getClientAreaLookup(this.selectedClientId)
            .subscribe({
                next: (clientAreas) => {
                    if (!clientAreas.length) {
                        this.toastService.show('No policies found for the selected client', 'info');
                        return;
                    }

                    this.clientAreas = clientAreas;
                },
                error: () => {
                    this.toastService.show('Failed to load client areas', 'error');
                }
            }
        );
    }

    getWorksheets(): void {
        this.loading = true;
        this.workbookService.getSheetNames(`${this.batch.filePath}\\${this.batch.storedName}`)
            .subscribe({
                next: (data) => {
                    this.worksheets = data.results || [];

                    if (!this.worksheetSelection || this.worksheetSelection['length'] < 1) {
                        this.worksheets.forEach(worksheet => {
                            const worksheetSelectionItem = new AgeingWorksheetSelectionItem();

                            worksheetSelectionItem.worksheet = worksheet;
                            worksheetSelectionItem.selected = false;
                            worksheetSelectionItem.clientAreaId = undefined;

                            this.worksheetSelection['push'](worksheetSelectionItem);
                        });
                    }
                },
                error: (error) => {
                    if (error.status === 404) {
                        const errorBody = error.response ? JSON.parse(error.response) : null;
                        if (errorBody?.code === 'FILE_NOT_FOUND') {
                            this.toastService.show('Excel file not found. Delete this import batch and upload again.', 'warning');
                        } else {
                            this.toastService.show('File not found. Please try again.', 'info');
                        }
                    } else {
                        this.errorService.handleError(error);
                    }
                    this.loading = false;
                },
                complete: () => {
                    this.loading = false;
                }
            });
    }

    isValid(): boolean {
        this.isWorksheetSelected = this.worksheetSelection.some((selection: AgeingWorksheetSelectionItem) => selection.selected);

        if (!this.isWorksheetSelected) {
            return false;
        }

        if (this.form.invalid) {
            return false;
        }

        return true;
    }

    onActivate(): void {
        this.route.paramMap.subscribe(params => {
            this.batchId = Number(params.get('id'));

            if (this.batchId > 0) {
                this.getBatch();
            }
        });
    }

    onNext(): Observable<boolean> {
        return new Observable<boolean>(observer => {
            const worksheetsToSave = this.worksheetSelection.map(selection => {
                return new AgeingWorksheetInput({
                    clientAreaId: selection.clientAreaId,
                    importRangeMappingId: undefined,
                    worksheetName: selection.worksheet,
                    isSelected: selection.selected,
                });
            });

            let saveCount = 0;

            worksheetsToSave.forEach(worksheetInput => {
                this.ageingService.createAgeingWorksheet(this.batchId, worksheetInput)
                    .subscribe({
                        next: () => {
                            saveCount++;

                            if (saveCount === worksheetsToSave.length) {
                                this.toastService.show('Ageing worksheets saved successfully', 'Success');

                                // Sheet selection changed — tell import step to re-run
                                const mappingsChangedKey = `ageing_mappings_changed_${this.batchId}`;
                                sessionStorage.setItem(mappingsChangedKey, 'true');

                                observer.next(true);
                                observer.complete();
                            }
                        },
                        error: (error: any) => {
                            if (error.status === 400) {
                                const errorBody = error.response ? JSON.parse(error.response) : null;
                                const message = errorBody?.message || 'Bad request. Please try again.';
                                this.toastService.show(message, 'Info');
                            }
                            else {
                                this.errorService.handleError(error);
                            }

                            observer.next(false);
                            observer.complete();
                        }
                    }
                );
            });
        });
    }

    onPrevious(): Observable<boolean> {
        return scheduled([true], asyncScheduler);
    }
}
